/* eslint-disable camelcase */

import states from '@config/states'
import { convertToSlug } from '@utils/slugs'

function pickAttribute(attributes, keys) {
  if (!Array.isArray(attributes)) return null
  const hit = keys
    .map((key) => attributes.find((attr) => attr && attr.key === key && attr.value != null && String(attr.value).length))
    .find(Boolean)
  return hit ? String(hit.value) : null
}

/** @param {unknown} raw */
function mapReservables(raw) {
  if (!Array.isArray(raw)) return []
  return raw.map((r) => {
    if (!r || typeof r !== 'object') return null
    const cd = r.courseDetail && typeof r.courseDetail === 'object' ? r.courseDetail : {}
    return {
      id: r.id != null ? String(r.id) : null,
      name: r.name != null ? String(r.name) : null,
      holes: cd.holes ?? r.holes ?? null,
      openToPublic: cd.openToPublic ?? r.openToPublic ?? null,
      walkingAvailable: cd.walkingAvailable ?? r.walkingAvailable ?? null,
    }
  }).filter(Boolean)
}

function pickWebsiteUrl(detail) {
  if (detail.websiteUrl != null && String(detail.websiteUrl).trim()) {
    return String(detail.websiteUrl).trim()
  }
  return pickAttribute(detail.attributes, ['website', 'websiteUrl', 'url'])
}

/** Map SDK/profile type tokens onto Schema.org @type values crawlers recognize. */
const SCHEMA_ORG_TYPE_BY_TOKEN = {
  golf_course: 'GolfCourse',
  driving_range: 'SportsActivityLocation',
  lessons: 'SportsActivityLocation',
  simulators: 'SportsActivityLocation',
}

/**
 * @param {unknown} type
 * @returns {string|null}
 */
function mapSchemaOrgTypeToken(type) {
  if (typeof type !== 'string') return null
  const trimmed = type.trim()
  if (!trimmed) return null
  // Already Schema.org-style (GolfCourse, PostalAddress, …)
  if (/^[A-Z][A-Za-z0-9]*$/.test(trimmed)) return trimmed
  const key = trimmed.toLowerCase().replace(/[\s-]+/g, '_')
  return SCHEMA_ORG_TYPE_BY_TOKEN[key] ?? null
}

/**
 * @param {unknown} node
 * @returns {Record<string, unknown>|null}
 */
function normalizeSchemaOrgNode(node) {
  if (!node || typeof node !== 'object' || Array.isArray(node)) return null
  if (node['@type'] == null) return null
  const next = { ...node }
  if (Array.isArray(next['@type'])) {
    const mapped = next['@type'].map(mapSchemaOrgTypeToken).filter(Boolean)
    if (!mapped.length) return null
    next['@type'] = mapped.length === 1 ? mapped[0] : mapped
  } else {
    const mapped = mapSchemaOrgTypeToken(next['@type'])
    if (!mapped) return null
    next['@type'] = mapped
  }
  return next
}

/**
 * @param {Record<string, unknown>} schemaOrg
 * @returns {boolean}
 */
function isUsableSchemaOrgDocument(schemaOrg) {
  if (!schemaOrg || typeof schemaOrg !== 'object' || Array.isArray(schemaOrg)) return false
  if (Array.isArray(schemaOrg['@graph'])) {
    return schemaOrg['@graph'].some((node) => node && typeof node === 'object' && !Array.isArray(node))
  }
  return Object.keys(schemaOrg).length > 0 && schemaOrg['@type'] != null
}

/**
 * True when a FacilityPage `schemaOrg` prop is safe to prefer over the hand-built GolfCourse block.
 * Defense in depth for non-adapter callers / incomplete props.
 *
 * @param {unknown} schemaOrg
 * @returns {boolean}
 */
export function isRenderableFacilitySchemaOrg(schemaOrg) {
  return isUsableSchemaOrgDocument(schemaOrg)
}

/**
 * Pulls JSON-LD from FacilityDetail.profile.schemaOrg.
 * Accepts a plain object or an array of nodes (normalized to `@graph`).
 * Maps SDK snake_case type tokens (e.g. `golf_course`) to Schema.org types.
 * Returns null when the payload is empty/invalid so FacilityPage can fall back.
 *
 * @param {unknown} profile
 * @returns {Record<string, unknown>|null}
 */
export function pickSchemaOrgFromProfile(profile) {
  if (!profile || typeof profile !== 'object' || Array.isArray(profile)) return null
  const { schemaOrg } = profile

  if (Array.isArray(schemaOrg)) {
    const nodes = schemaOrg.map(normalizeSchemaOrgNode).filter(Boolean)
    const doc = nodes.length ? { '@graph': nodes } : null
    return doc && isUsableSchemaOrgDocument(doc) ? doc : null
  }

  if (!schemaOrg || typeof schemaOrg !== 'object') return null

  if (Array.isArray(schemaOrg['@graph'])) {
    const nodes = schemaOrg['@graph'].map(normalizeSchemaOrgNode).filter(Boolean)
    if (nodes.length) {
      // Emit a graph document only — drop unsanitized root @type siblings from the API payload
      const {
        '@type': _rootType,
        '@graph': _rawGraph,
        ...rest
      } = schemaOrg
      const doc = { ...rest, '@graph': nodes }
      return isUsableSchemaOrgDocument(doc) ? doc : null
    }
    // Empty/unusable graph: fall through to a root entity if one is present
    const { '@graph': _emptyGraph, ...root } = schemaOrg
    const doc = normalizeSchemaOrgNode(root)
    return doc && isUsableSchemaOrgDocument(doc) ? doc : null
  }

  const doc = normalizeSchemaOrgNode(schemaOrg)
  return doc && isUsableSchemaOrgDocument(doc) ? doc : null
}

/**
 * Maps FacilityDetail from @pgahq/golf-facilities-js → props expected by FacilityPage / FacilityPageQuery merge
 *
 * Gaps vs legacy Algolia graph (no street line, photos, star rating, etc.) are nulled with notes below.
 *
 * @param {object} detail FacilityDetail from the golf facilities service
 * @returns {object}
 */
export function adaptFacilityDetailToFacilityPageProps(detail) {
  if (!detail || typeof detail !== 'object') {
    throw new TypeError('adaptFacilityDetailToFacilityPageProps expects a detail object')
  }

  const addr = detail.address || {}
  const city = addr.city ?? null
  const region = addr.region ? String(addr.region).toUpperCase() : null
  const zip = addr.postalCode ?? null
  const state = region
  const state_name = region ? states[region] ?? null : null

  const line1 = addr.line1 != null ? String(addr.line1).trim() || null : null
  const address1 = pickAttribute(detail.attributes, ['address1', 'street', 'streetAddress', 'address']) ?? line1
  const address2Parts = [city, region, zip].filter(Boolean)
  const address2 = address2Parts.length ? address2Parts.join(' ') : null

  const operations_type_label = pickAttribute(detail.attributes, ['operations_type_label', 'operationsType', 'venueType'])
    ?? (Array.isArray(detail.kinds) && detail.kinds[0]?.kind ? String(detail.kinds[0].kind) : null)

  const prices = pickAttribute(detail.attributes, ['prices', 'green_fees', 'greenFees']) ?? null

  const courseSlug = detail.slug
    ?? (detail.name ? convertToSlug(detail.name) : null)

  const publicId = detail.publicId != null ? String(detail.publicId).trim() : ''
  const pgaId = detail.pgaId != null ? String(detail.pgaId).trim() : ''

  return {
    facility_name: detail.name ?? null,
    address1,
    address2,
    zip,
    phone: detail.phone ?? null,
    website_url: pickWebsiteUrl(detail),
    reservables: mapReservables(detail.reservables),
    // Facility imagery is not part of FacilityDetail from the package schema
    photo_facility: null,
    prices,
    state,
    state_name,
    city,
    operations_type_label,
    // URL segment after city in /play/[state]/[city]/[course]/[id]
    course: courseSlug,
    id: publicId || pgaId || String(detail.id),
    // Not present on FacilityDetail
    star_rating: null,
    // SDK profile.schemaOrg JSON-LD (preferred over the hand-built GolfCourse block)
    schemaOrg: pickSchemaOrgFromProfile(detail.profile),
    /* Additional legacy graph fields intentionally omitted or unknown on FacilityDetail:
     * address3, address4, universal_id, course_type_label (set omitted — consumers should tolerate undefined)
     */
  }
}
